import { Link } from "@tanstack/react-router";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import { HelpMobileMenu } from "@/components/marketing/help-mobile-menu";
import { HELP_ARTICLES, type HelpArticle } from "@/lib/help-content";
import { useAuth } from "@/hooks/use-auth";

const LOGO_LIGHT = logoLight.url;

export const NAVY = "#0B2545";
export const NAVY_DEEP = "#07182E";
export const PAPER = "#F4F1E8";
export const PAPER_2 = "#FBFAF5";
export const GOLD = "#C9A24B";
export const INK = "#14213A";
export const INK_2 = "#545C6B";
export const LINE = "#E4DECF";
export const NAVY_TEXT = "#EDEAE0";
export const NAVY_TEXT_DIM = "#B6BFCD";

export const FONT_SERIF = "'Spectral', Georgia, serif";
export const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
export const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

/**
 * Shared article shell for /help/$slug and for standalone help routes that
 * need their own head metadata (for example /help/email-connections).
 */
export function HelpArticleLayout({ article }: { article: HelpArticle }) {
  return (
    <main
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased"
    >
      <HelpNav />
      <section
        style={{
          background: PAPER_2,
          padding: "72px 0 40px",
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <div className="mx-auto" style={{ maxWidth: 820, padding: "0 32px" }}>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 14,
            }}
          >
            <Link to="/help" style={{ color: GOLD, textDecoration: "none" }}>
              Help
            </Link>
            <span style={{ color: INK_2, margin: "0 8px" }} aria-hidden>
              /
            </span>
            <span style={{ color: INK_2 }}>{article.category}</span>
          </div>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(32px,3.8vw,48px)",
              lineHeight: 1.08,
              letterSpacing: "-.01em",
              margin: 0,
            }}
          >
            {article.title}
          </h1>
          <p
            style={{
              marginTop: 16,
              maxWidth: 680,
              color: INK_2,
              fontSize: 17,
              lineHeight: 1.6,
            }}
          >
            {article.summary}
          </p>
        </div>
      </section>

      <section style={{ padding: "56px 0 80px" }}>
        <div className="mx-auto" style={{ maxWidth: 820, padding: "0 32px" }}>
          {article.sections.map((s) => (
            <div key={s.id} style={{ marginBottom: 40, scrollMarginTop: 24 }} id={s.id}>
              <h2
                style={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 600,
                  fontSize: 24,
                  lineHeight: 1.25,
                  margin: "0 0 12px",
                  color: INK,
                }}
              >
                <a href={`#${s.id}`} style={{ color: "inherit", textDecoration: "none" }}>
                  {s.heading}
                </a>
              </h2>
              {s.body.split(/\n\s*\n/).map((para, i) => (
                <p
                  key={i}
                  style={{
                    fontFamily: FONT_SANS,
                    fontSize: 16,
                    lineHeight: 1.7,
                    color: INK_2,
                    margin: "0 0 14px",
                    whiteSpace: "pre-line",
                  }}
                >
                  {para}
                </p>
              ))}
            </div>
          ))}

          <RelatedArticles current={article} />

          <div
            style={{
              marginTop: 40,
              display: "flex",
              flexWrap: "wrap",
              gap: 28,
              alignItems: "center",
            }}
          >
            <Link
              to="/help"
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: INK,
                borderBottom: `2px solid ${GOLD}`,
                paddingBottom: 3,
                textDecoration: "none",
                fontFamily: FONT_SANS,
              }}
            >
              Back to help center
            </Link>
            <Link
              to="/contact"
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: INK,
                borderBottom: `2px solid ${GOLD}`,
                paddingBottom: 3,
                textDecoration: "none",
                fontFamily: FONT_SANS,
              }}
            >
              Still stuck? Contact us
            </Link>
          </div>
        </div>
      </section>

      <HelpFooter />
    </main>
  );
}

function RelatedArticles({ current }: { current: HelpArticle }) {
  const related = HELP_ARTICLES.filter(
    (a) => a.category === current.category && a.slug !== current.slug,
  );
  if (related.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 48,
        paddingTop: 24,
        borderTop: `1px solid ${LINE}`,
      }}
    >
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 12,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: GOLD,
          marginBottom: 12,
        }}
      >
        More in {current.category}
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {related.map((a) => (
          <li key={a.slug} style={{ padding: "8px 0" }}>
            <Link
              to="/help/$slug"
              params={{ slug: a.slug }}
              style={{
                fontFamily: FONT_SERIF,
                fontSize: 17,
                fontWeight: 600,
                color: INK,
                textDecoration: "none",
                borderBottom: `1px solid ${LINE}`,
              }}
            >
              {a.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HelpNav() {
  const { session, loading } = useAuth();
  const isAuthed = Boolean(session);
  return (
    <header
      style={{
        background: NAVY,
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
        position: "relative",
      }}
    >
      <style>{`
        @media (max-width: 1023.98px) {
          .cd-helpnav-desktop { display: none !important; }
          .cd-helpnav-mobile { display: inline-flex !important; }
        }
      `}</style>
      <div
        className="mx-auto"
        style={{
          maxWidth: 1200,
          padding: "18px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <Logo
            src={LOGO_LIGHT}
            alt="CapitalDart"
            height={34}
            style={{ display: "block" }}
            trademarkClassName="text-[#F4F1E8]"
          />
        </Link>
        <nav
          className="cd-helpnav-desktop"
          style={{ display: "flex", alignItems: "center", gap: 28 }}
        >
          {loading ? null : isAuthed ? (
            <>
              <Link
                to="/help"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  textDecoration: "none",
                }}
              >
                All articles
              </Link>
              <Link
                to="/dashboard"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  textDecoration: "none",
                }}
              >
                Back to dashboard
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/help"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  textDecoration: "none",
                }}
              >
                All articles
              </Link>
              <Link
                to="/auth"
                search={{ mode: "signup" as const }}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  background: GOLD,
                  color: NAVY_DEEP,
                  padding: "10px 18px",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                Get started
              </Link>
            </>
          )}
        </nav>
        <HelpMobileMenu className="cd-helpnav-mobile" isAuthed={isAuthed} loading={loading} />
      </div>
    </header>
  );
}

export function HelpFooter() {
  return (
    <footer
      style={{
        background: NAVY_DEEP,
        color: NAVY_TEXT,
        padding: "48px 0",
        textAlign: "center",
      }}
    >
      <div className="mx-auto" style={{ maxWidth: 1200, padding: "0 32px" }}>
        <Logo
          src={LOGO_LIGHT}
          alt="CapitalDart"
          height={26}
          style={{
            display: "inline-block",
            marginBottom: 14,
          }}
          trademarkClassName="text-[#F4F1E8]"
        />
        <div
          style={{
            fontFamily: FONT_SANS,
            fontSize: 13,
            color: "#7E8A9C",
          }}
        >
          © {new Date().getFullYear()} CapitalDart. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
